import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiClientError } from '../api/client.js';
import { useAuth } from '../store/AuthContext.js';
import { AuthCard } from '../ui/AuthCard.js';
import { Button } from '../ui/Button.js';
import { Field } from '../ui/Field.js';
import { t } from '../i18n/index.js';

export function LoginPage() {
  const labels = t();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  /** Page demandee avant la redirection vers la connexion. */
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setBusy(true);

    try {
      await auth.login({ email, password });
      void navigate(redirectTo, { replace: true });
    } catch (cause) {
      if (cause instanceof ApiClientError) {
        setError(cause.message);
        setFieldErrors(
          Object.fromEntries(cause.issues.map((issue) => [issue.path, issue.message])),
        );
      } else {
        setError(labels.errors.unexpected);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title={labels.auth.login.title}
      subtitle={labels.auth.login.subtitle}
      footer={
        <>
          {labels.auth.login.noAccount}{' '}
          <Link to="/inscription">{labels.auth.login.createAccount}</Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="fg-alert fg-alert--error" role="alert">
            {error}
          </div>
        )}

        <Field
          label={labels.auth.emailLabel}
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrors.email}
          disabled={busy}
        />

        <Field
          label={labels.auth.passwordLabel}
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password}
          disabled={busy}
        />

        <div className="fg-auth__actions">
          <Button type="submit" busy={busy}>
            {labels.auth.login.submit}
          </Button>
          <div className="fg-auth__link-row">
            <Link to="/mot-de-passe-oublie">{labels.auth.login.forgot}</Link>
          </div>
        </div>
      </form>
    </AuthCard>
  );
}
